import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { workspaceSummary } from '../../lib/workspace-summary'
import { RiArchiveLine, RiInboxUnarchiveLine } from '@remixicon/react'
import type { TimelineEvent, Workspace, WorkspaceDetailResponse } from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  PageHeader,
  SectionSpinner,
  Tabs,
  TabsList,
  Tab,
  TabsPanel,
} from '../../components/ui'
import Timeline from '../../components/Timeline'
import ProfileProposalsPanel from '../../components/workspace/ProfileProposalsPanel'
import WorkspaceMembersPanel from '../../components/workspace/WorkspaceMembersPanel'
import WorkspaceReportsPanel from '../../components/workspace/WorkspaceReportsPanel'
import WorkspaceSurveysPanel from '../../components/workspace/WorkspaceSurveysPanel'
import WorkspaceTasksPanel from '../../components/workspace/WorkspaceTasksPanel'
import WorkspaceProfileForm, {
  fromWorkspace,
  toApiPayload,
  type WorkspaceProfileValues,
} from '../../components/workspace/WorkspaceProfileForm'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

export default function WorkspaceDetail() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  // Handed over by the creation page when the initial survey was wanted but could not be made.
  const kickoffFailed = Boolean((useLocation().state as { kickoffFailed?: boolean } | null)?.kickoffFailed)
  const { workspaceId } = useParams<{ workspaceId: string }>()

  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [events, setEvents] = useState<TimelineEvent[]>([])
  const [profile, setProfile] = useState<WorkspaceProfileValues | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadWorkspace = useCallback(async () => {
    const response = await api.get<WorkspaceDetailResponse>(`/api/workspaces/${workspaceId}`)
    setWorkspace(response.data)
    setProfile(fromWorkspace(response.data))
  }, [workspaceId])

  useEffect(() => {
    if (!workspaceId) return

    Promise.all([
      loadWorkspace(),
      api
        .get<{ data: TimelineEvent[] }>(`/api/workspaces/${workspaceId}/timeline`)
        .then((response) => setEvents(response.data)),
    ])
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [workspaceId, loadWorkspace])

  const handleSaveProfile = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!profile || !workspaceId) return

    setSaving(true)
    setError(null)
    try {
      await api.put(`/api/workspaces/${workspaceId}`, toApiPayload(profile))
      await loadWorkspace()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  const toggleArchive = async () => {
    if (!workspace) return
    const action = workspace.status === 'archived' ? 'unarchive' : 'archive'

    setError(null)
    try {
      await api.post(`/api/workspaces/${workspace.id}/${action}`, {})
      await loadWorkspace()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  if (loading) return <SectionSpinner />

  if (!workspace || !profile) {
    return (
      <EmptyState
        title={t('errors.notFound')}
        description={error ?? undefined}
        action={
          <Button variant="secondary" onClick={() => navigate('/app/workspaces')}>
            {t('common.back')}
          </Button>
        }
      />
    )
  }

  return (
    <>
      <PageHeader
        title={workspace.name}
        subtitle={
          workspaceSummary(workspace, t) || undefined
        }
        actions={
          <Button variant="secondary" onClick={toggleArchive}>
            {workspace.status === 'archived' ? (
              <>
                <RiInboxUnarchiveLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t('workspaces.unarchive')}
              </>
            ) : (
              <>
                <RiArchiveLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {t('workspaces.archive')}
              </>
            )}
          </Button>
        }
      />

      {workspace.status === 'archived' && (
        <div className="mb-6">
          <Badge variant="neutral">{t('workspaces.archived')}</Badge>
        </div>
      )}

      {kickoffFailed && <Alert className="mb-6">{t('workspaces.kickoffFailed')}</Alert>}
      {error && <Alert className="mb-6">{error}</Alert>}

      <Tabs defaultValue="timeline">
        <TabsList className="mb-6 w-full">
          <Tab value="timeline">{t('workspaces.tabs.timeline')}</Tab>
          <Tab value="surveys">{t('workspaces.tabs.surveys')}</Tab>
          <Tab value="reports">{t('workspaces.tabs.reports')}</Tab>
          <Tab value="tasks">{t('workspaces.tabs.tasks')}</Tab>
          <Tab value="members">{t('workspaces.tabs.members')}</Tab>
          <Tab value="profile">{t('workspaces.tabs.profile')}</Tab>
        </TabsList>

        <TabsPanel value="timeline">
          <Card className="p-6">
            {events.length === 0 ? (
              <EmptyState
                title={t('timeline.emptyTitle')}
                description={t('timeline.emptyOperatorDescription')}
              />
            ) : (
              <Timeline events={events} />
            )}
          </Card>
        </TabsPanel>

        <TabsPanel value="surveys">
          <WorkspaceSurveysPanel workspaceId={workspace.id} />
        </TabsPanel>

        <TabsPanel value="reports">
          <WorkspaceReportsPanel workspaceId={workspace.id} />
        </TabsPanel>

        <TabsPanel value="tasks">
          <WorkspaceTasksPanel workspaceId={workspace.id} />
        </TabsPanel>

        <TabsPanel value="members">
          <WorkspaceMembersPanel workspaceId={workspace.id} canManage />
        </TabsPanel>

        <TabsPanel value="profile">
          <ProfileProposalsPanel
            workspaceId={workspace.id}
            current={workspace}
            onAccepted={() => loadWorkspace().catch((err) => setError(toErrorMessage(err)))}
          />
          <Card className="p-6">
            <form onSubmit={handleSaveProfile} className="space-y-6">
              <WorkspaceProfileForm values={profile} onChange={setProfile} disabled={saving} />
              <div className="flex justify-end border-t border-gray-200 pt-6 dark:border-gray-800">
                <Button type="submit" loading={saving}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          </Card>
        </TabsPanel>
      </Tabs>
    </>
  )
}
